import { useEffect, useMemo, useRef, useState } from 'react'

import { useNodeStore } from '@/stores/nodeStore'
import { useNodeHistoryStore } from '@/stores/nodeHistoryStore'
import { useFontsLoader } from '@/hooks/editor/useFontLoader'
import { useClickOutside } from '@/hooks/useOutsideClick'

import { CanvasEditor as CanvasEditorComponent } from '@/components/modules/CanvasEditor'

import { type WorkspaceMedia } from '@backend/lib/db/typeRoots/workspaceMedia'
import { Color as CanvasColor } from '@/components/modules/CanvasEditor/color'
import { type Color } from '@backend/lib/db/typeRoots/color'

import { calculateCanvasSize } from '@/lib/editor'

const DEFAULT_BG_COLOR: Color = { r: 255, g: 255, b: 255, a: 255 }

type Props = {
  panAndZoom: { scale: number; x: number; y: number }
  canvasMedia: Extract<WorkspaceMedia, { type: 'canvas' }>
  backgroundColor?: Color
  overlayOpacity?: number
  onCanvasSizeChange: (size: { width: number; height: number }) => void
  onCanvasAspectChange: (aspect: string) => void
  inputButtonRef: React.RefObject<HTMLDivElement | null>
  isHoveringQuickmenu: boolean
  setIsHoveringQuickmenu: (value: boolean) => void
  quickMenuRef: React.RefObject<HTMLDivElement | null>
}

export const WorkspaceEditorCanvas = ({
  panAndZoom,
  canvasMedia,
  backgroundColor,
  overlayOpacity,
  onCanvasSizeChange,
  onCanvasAspectChange,
  inputButtonRef,
  isHoveringQuickmenu,
  setIsHoveringQuickmenu,
  quickMenuRef,
}: Props) => {
  // refs

  const canvasContainerRef = useRef<HTMLDivElement>(null)

  // state

  const [canvasSize, setCanvasSize] = useState<{ width: number; height: number }>()

  // hooks

  const { fontsLoaded } = useFontsLoader()
  const { resetHistory } = useNodeHistoryStore()
  const { setNodes, setSelectedNodeIndex } = useNodeStore()

  useClickOutside(
    [canvasContainerRef, inputButtonRef, quickMenuRef],
    (_event) => {
      setSelectedNodeIndex(-1)
    },
    { checkPopovers: true },
  )

  // effects

  // initialize canvas and nodes — keyed on identity-level fields, not the whole object
  // (backgroundColor changes should not re-initialize)
  const canvasNodes = canvasMedia.nodes
  const canvasAspect = canvasMedia.aspectRatio
  useEffect(() => {
    if (!fontsLoaded) return
    resetHistory()
    setNodes(canvasNodes, { history: true })
    const size = calculateCanvasSize(canvasAspect)
    setCanvasSize(size)
    onCanvasAspectChange(canvasAspect)
    onCanvasSizeChange(size)
  }, [
    canvasNodes,
    canvasAspect,
    fontsLoaded,
    setNodes,
    resetHistory,
    onCanvasAspectChange,
    onCanvasSizeChange,
  ])

  // auto fit when canvasSize changes
  useEffect(() => {
    if (!canvasSize) return
    // fitAllCanvases()
  }, [canvasSize])

  // render

  const canvasEditor = useMemo(
    () =>
      canvasSize && (
        <CanvasEditorComponent
          width={canvasSize.width}
          height={canvasSize.height}
          backgroundColor={CanvasColor(backgroundColor ?? DEFAULT_BG_COLOR)}
          panX={panAndZoom.x}
          panY={panAndZoom.y}
          zoom={panAndZoom.scale}
          overlayOpacity={overlayOpacity}
          isHoveringQuickmenu={isHoveringQuickmenu}
          setIsHoveringQuickmenu={setIsHoveringQuickmenu}
        />
      ),

    [
      panAndZoom.x,
      panAndZoom.y,
      panAndZoom.scale,
      overlayOpacity,
      isHoveringQuickmenu,
      canvasSize,
      setIsHoveringQuickmenu,
      backgroundColor,
    ],
  )

  return (
    <div
      ref={canvasContainerRef}
      className="animate-fade-in animate-once animate-duration-500 animate-ease-in animate-fill-forwards h-full w-full">
      {canvasEditor}
    </div>
  )
}
